import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useUserTimezone } from '@/modules/auth/hooks/useUserTimezone'
import {
  createDefaultPreferences,
  fetchUserPushPreferences,
  upsertUserPushPreferences,
  type UserPushPreferences,
  type PushScheduleMode,
  type PushDayKey,
  type PushDayConfig,
} from '../services/pushPreferencesService'

export type { UserPushPreferences, PushScheduleMode, PushDayKey, PushDayConfig } from '../services/pushPreferencesService'

export function usePushPreferences() {
  const { user } = useAuth()
  const { timezone: userTimezone } = useUserTimezone()

  const [prefs, setPrefs] = React.useState<UserPushPreferences | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const userId = user?.id || null

  const load = React.useCallback(async () => {
    if (!userId) {
      setPrefs(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const existing = await fetchUserPushPreferences(userId)
      if (existing) {
        setPrefs(existing)
      } else {
        const tz = userTimezone || 'Etc/UTC'
        setPrefs(createDefaultPreferences(userId, tz))
      }
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar preferencias de notificacao')
    } finally {
      setLoading(false)
    }
  }, [userId, userTimezone])

  React.useEffect(() => {
    load()
  }, [load])

  async function save(next?: UserPushPreferences): Promise<boolean> {
    if (!userId || !prefs) return false
    setSaving(true)
    setError(null)
    try {
      const target = next || prefs
      const updated = await upsertUserPushPreferences({
        userId,
        timezone: target.timezone,
        mode: target.mode,
        defaultStartMin: target.defaultStartMin,
        defaultEndMin: target.defaultEndMin,
        days: target.days,
        snoozeUntil: target.snoozeUntil,
      })
      setPrefs(updated)
      return true
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao salvar preferencias de notificacao')
      return false
    } finally {
      setSaving(false)
    }
  }

  function update(updater: (current: UserPushPreferences) => UserPushPreferences) {
    setPrefs((current) => (current ? updater(current) : current))
  }

  return {
    prefs,
    loading,
    saving,
    error,
    reload: load,
    save,
    update,
  }
}

